/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 * config.js
 */
import { InlineEditor } from "@ckeditor/ckeditor5-editor-inline";

import { Alignment } from "@ckeditor/ckeditor5-alignment";
import { Autoformat } from "@ckeditor/ckeditor5-autoformat";
// import { Autosave } from "@ckeditor/ckeditor5-autosave";
import { Bold, Italic, Underline } from "@ckeditor/ckeditor5-basic-styles";
import { BlockQuote } from "@ckeditor/ckeditor5-block-quote";
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import {
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
} from "@ckeditor/ckeditor5-font";
import { Heading } from "@ckeditor/ckeditor5-heading";
import { Highlight } from "@ckeditor/ckeditor5-highlight";
import { HorizontalLine } from "@ckeditor/ckeditor5-horizontal-line";
// import { GeneralHtmlSupport } from "@ckeditor/ckeditor5-html-support";
// import { Indent } from "@ckeditor/ckeditor5-indent";
import { Link } from "@ckeditor/ckeditor5-link";
import { List } from "@ckeditor/ckeditor5-list";
// import { MediaEmbed } from "@ckeditor/ckeditor5-media-embed";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
import { RemoveFormat } from "@ckeditor/ckeditor5-remove-format";
import { SpecialCharacters, SpecialCharactersEssentials  } from "@ckeditor/ckeditor5-special-characters";
// import {
//   SpecialCharacters,
//   SpecialCharactersArrows,
//   SpecialCharactersCurrency,
//   SpecialCharactersEssentials,
//   SpecialCharactersLatin,
//   SpecialCharactersMathematical,
//   SpecialCharactersText,
// } from "@ckeditor/ckeditor5-special-characters";
// import { TextTransformation } from "@ckeditor/ckeditor5-typing";
import { Undo } from "@ckeditor/ckeditor5-undo";

// Simplified FontFamilylist and fontSizeConfig
const FontFamilylist = [
  "Poppins",
  "Abril Fatface",
  "Alata",
  "Arial",
  "Bitter",
  "Cabin",
  "Courier New",
  "Droid Serif",
  "Georgia",
  "Helvetica Neue",
  "Inter",
  "Lato",
  "Lucida Sans Unicode",
  "Merriweather",
  "Montserrat",
  "Nunito",
  "Open Sans",
  "Oswald",
  "Oxygen",
  "Permanent Marker",
  "Roboto",
  "Roboto Slab",
  "Source Sans Pro",
  "Tahoma",
  "TimesNewRoman",
  "Trebuchet MS",
  "Ubuntu",
  "Verdana",
];
const fontSizeConfig = [8, 10, 12, 14, 15, 16, 18, 20, 22, 24, 25, 26, 28, 30, 32, 34, 35, 36, 38, 40, 42, 44, 45, 46, 48, 50, 52, 54, 55, 56, 58, 60, 62, 64, 66, 68, 70, 72, 74, 75];

// Streamlined heading configuration
const headingConfig = {
	options: [
		{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
		{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
		{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
		{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
		{ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
		{ model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
		{ model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
	]
};

// Inline editor configuration with only essential plugins
export const inlineEditorConfig = {
  plugins: [
    Alignment,
    Autoformat,
    BlockQuote,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    Heading,
    Highlight,
    HorizontalLine,
    Italic,
    Link,
    List,
    Paragraph,
    RemoveFormat,
    SpecialCharacters,
    Undo,
    Underline,
  ],
  toolbar: {
    items: [
      "undo",
      "redo",
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "link",
      "|",
      {
        label: "Fonts",
        icon: "text",
        items: [
          "fontSize",
          "fontFamily",
          "fontColor",
          "fontBackgroundColor",
          //"lineHeight",
        ],
      },
      "|",
      "alignment",
      "bulletedList",
      "numberedList",
      '|',
      "highlight",
      "horizontalLine",
      "removeFormat",
      "specialCharacters",
      "|",
      "blockQuote",
    ],
    focus: false,
    shouldNotGroupWhenFull: true
  },
  fontSize: { options: fontSizeConfig },
  fontFamily: { options: FontFamilylist },
  heading: headingConfig,
  link: {
    decorators: {
        isExternal: {
            mode: 'manual',
            label: 'Open in a new tab',
            defaultValue: false, // Set to true if you want the checkbox to be checked by default
            attributes: {
                target: '_blank',
                rel: 'noopener noreferrer'
            }
        }
    }
}
};

// Initialize CKEditor instances
window.editorElementMap = new Map(); // CKEditor instance map

window.destroyCKEditor = (instancesKey) => {
  const editor_instances = editorElementMap.get(instancesKey);
  if (editor_instances) {
    editor_instances.destroy();
    window.editorElementMap.delete(instancesKey);
  }
};

window.ckinlinEditor = (element) => {
  let ele_ID = element.id;
    InlineEditor.create(element, inlineEditorConfig)
      .then((editor) => {
        //editor.focus(); // Set focus to the editor instance
        window.ckinlinEditorInstance = editor; // Store CKEditor instance globally
        window.editorElementMap.set(ele_ID, editor); // Store CKEditor instance in the map with the element ID as the key
      })
      .catch((error) => {
        console.error(error);
      });
};
